import React, { useMemo, useState } from "react";
import { initialState, HMIContext } from "./HMIContext";
import { HMIView, IHMIProps } from "./HMIContextInterfaces";
import { IFailureCode, IFailureType } from "../HMICachedDataContext/HMICachedDataContextInterfaces";
import {
    IArea,
    IFunctionalLocation,
} from "../../pages/HMI/HMIDashboard/components/FailureDetails/FailureDetailsInterfaces";
import {
    removeCurrentArea,
    removeCurrentFunctionalLocation,
    setCurrentNotes,
    removeCurrentNotes,
    setCurrentOperator,
    setCurrentFailureDetailsStep,
    getCurrentOperator,
} from "../../utilities/storageUtilities";
import { FAILUREDETAILSSTEPS } from "constants/constants";
import { ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";
import { ViewOptionsEnum } from "components/ViewDropdown/ViewDropdown.view";

export const HMIProvider: React.FC<IHMIProps> = ({ children }) => {
    const [view, setView] = useState<ViewOptionsEnum>(initialState.view);
    const [shift, setShift] = useState<ShiftOptionsEnum>(initialState.shift);
    const [displayUnit, setDisplayUnit] = useState(initialState.displayUnit);
    const [operator, setOperator] = useState(initialState.operator);
    const [failureType, setFailureType] = useState(initialState.failureType);
    const [area, setArea] = useState(initialState.area);
    const [functionalLocation, setFunctionalLocation] = useState(initialState.functionalLocation);
    const [failureDetailsStep, setFailureDetailsStep] = useState(initialState.failureDetailsStep);
    const [failureCode, setFailureCode] = useState(initialState.failureCode);
    const [notes, setNotes] = useState(initialState.notes);

    const providerValue = useMemo<HMIView>(() => {
        const handleView = (value: ViewOptionsEnum): void => {
            setView(value);
        };

        const handleShift = (value: ShiftOptionsEnum): void => {
            setShift(value);
        };

        const handleDisplayUnit = (value: string): void => {
            setDisplayUnit(value);
        };

        const handleOperator = (operator: string | undefined): void => {
            setOperator(operator);
            setCurrentOperator(operator);
        };

        const handleFailureTypeChange = (failureType: IFailureType) => {
            setFailureType(failureType);
        };
        const handleArea = (area: IArea | undefined) => {
            if (!area) {
                removeCurrentArea();
            }
            setArea(area);
        };

        const handleFunctionalLocation = (functionalLocation: IFunctionalLocation | undefined) => {
            if (!functionalLocation) {
                removeCurrentFunctionalLocation();
            }
            setFunctionalLocation(functionalLocation);
        };

        const handleFailureCode = (failureCode: IFailureCode) => {
            setFailureCode(failureCode);
        };

        const handleNotes = (notes?: string) => {
            setNotes(notes!);

            if (!notes) {
                removeCurrentNotes();
            } else {
                setCurrentNotes(notes);
            }
        };

        const handlePreviousFailureDetailsStep = () => {
            if (failureDetailsStep > FAILUREDETAILSSTEPS.FAILURETYPES) {
                setFailureDetailsStep((failureDetailsStep) => {
                    setCurrentFailureDetailsStep(failureDetailsStep - 1);
                    return failureDetailsStep - 1;
                });
            }
        };

        const handleNextFailureDetailsStep = () => {
            setFailureDetailsStep((failureDetailsStep) => {
                setCurrentFailureDetailsStep(failureDetailsStep + 1);
                return failureDetailsStep + 1;
            });
        };

        const handleSetFailureDetailStep = (value: number) => {
            setFailureDetailsStep(value);
            setCurrentFailureDetailsStep(value);
        };

        const resetEditingSelections = () => {
            setFailureType(initialState.failureType);
            setArea(initialState.area);
            setFunctionalLocation(initialState.functionalLocation);
            setFailureDetailsStep(initialState.failureDetailsStep);
            setFailureCode(initialState.failureCode);
            setNotes(initialState.notes);
        };

        const resetHMIContextProvider = () => {
            const currentOperator = getCurrentOperator();
            setView(initialState.view);
            setShift(initialState.shift);
            setDisplayUnit(initialState.displayUnit);
            setOperator(currentOperator);
            resetEditingSelections();
        };

        return {
            view,
            handleView,
            shift,
            handleShift,
            displayUnit,
            handleDisplayUnit,
            operator,
            handleOperator,
            failureType,
            handleFailureTypeChange,
            area,
            handleArea,
            functionalLocation,
            handleFunctionalLocation,
            failureCode,
            handleFailureCode,
            failureDetailsStep,
            notes,
            handleNotes,
            handlePreviousFailureDetailsStep,
            handleNextFailureDetailsStep,
            handleSetFailureDetailStep,
            resetEditingSelections,
            resetHMIContextProvider,
        };
    }, [
        view,
        shift,
        displayUnit,
        operator,
        failureType,
        area,
        functionalLocation,
        failureCode,
        failureDetailsStep,
        notes,
    ]);

    return <HMIContext.Provider value={providerValue}>{children}</HMIContext.Provider>;
};
